import { createSelector, Selector as ReselectSelector } from 'reselect';

import invariant from '@rover/utilities/invariant';

import {
  ActionTypes,
  Dispatch,
  Duck,
  DuckOptions,
  EffectTypes,
  GlobalReduxState,
  Selector,
  SelectorTypes,
} from './types';

export const createDuck = <
  DuckState,
  DuckActions extends ActionTypes,
  DuckEffects extends EffectTypes,
  DuckSelectors extends SelectorTypes
>({
  initialState,
  rootSelector,
  name,
  actions,
  effects,
  selectors,
  externalActionHandlers,
}: DuckOptions<DuckState, DuckActions, DuckEffects, DuckSelectors>): Duck<
  DuckState,
  DuckActions,
  DuckEffects,
  DuckSelectors
> => {
  // Build the action creators using a namespaced
  // action type
  const duckActions: Partial<DuckActions> = {};
  Object.keys(actions).forEach((type: keyof DuckActions) => {
    const actionType = `${name}/${String(type)}`;
    // An `ActionCreator` is a function with `actionType` and `toString` attached. Built in
    // one expression so the incomplete intermediate value never has to be typed.
    duckActions[type] = Object.assign((payload: unknown) => ({ payload, type: actionType }), {
      actionType,
      toString: () => actionType,
    }) as DuckActions[keyof DuckActions];
  });

  // Build selectors giving them access to the state and each other
  const duckSelectors: Partial<DuckSelectors> = {};
  const selector = rootSelector || ((s: GlobalReduxState) => s[name]);
  const getDuckState: ReselectSelector<GlobalReduxState, DuckState> = createSelector(
    [selector],
    (state: DuckState): DuckState => {
      invariant(
        state,
        `You have a missing reducer for duck: ${name}.${'\n'}Register it in the page's <Provider> component. More information here: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/993396022/Connecting+components+to+state+management#The-Top-Level-Container`
      );
      return state;
    }
  );
  // Each factory receives the same `duckSelectors`, so a later selector can compose an
  // earlier one; reads happen after this loop, when the object is complete, which TypeScript
  // cannot see. The second cast picks the non-parametric shape, the only one of the two that
  // describes a three-argument call.
  Object.keys(selectors).forEach((type: keyof DuckSelectors) => {
    const createDuckSelector = selectors[type] as Selector<DuckState, unknown, DuckSelectors>;

    duckSelectors[type] = createDuckSelector(
      getDuckState,
      createSelector,
      duckSelectors as DuckSelectors
    ) as DuckSelectors[keyof DuckSelectors];
  });

  // Build the effects by passing them the internal
  // actions and effects
  const duckEffects: Partial<DuckEffects> = {};
  Object.keys(effects).forEach((type: keyof DuckEffects) => {
    // Cast for the same reason as the selectors above. The thunk built here is correct for
    // this key, but TS only sees `DuckEffects[keyof DuckEffects]`.
    duckEffects[type] = ((payload) =>
      (dispatch: Dispatch, getState: () => GlobalReduxState, extraArgs) => {
        const effect = effects[type](payload);
        return effect(
          dispatch,
          getState,
          duckActions as DuckActions,
          duckEffects as DuckEffects,
          duckSelectors as DuckSelectors,
          // Effects destructure this. A store built with plain `thunk` rather than
          // `withExtraArgument` supplies nothing, which would throw before the effect runs.
          extraArgs ?? {}
        );
      }) as DuckEffects[keyof DuckEffects];
  });

  // Create the reducer
  const reducer = (
    state: DuckState = initialState, // eslint-disable-line default-param-last
    action: { type: string; payload?: unknown }
  ): DuckState => {
    if (externalActionHandlers && externalActionHandlers[action.type]) {
      return externalActionHandlers[action.type](action)(state);
    }
    if (action.type.includes(name)) {
      const actionType: keyof ActionTypes = action.type.replace(`${name}/`, '');
      const reduce: ((a) => (s: Readonly<DuckState>) => DuckState) | undefined =
        actions[actionType];
      if (reduce) {
        return reduce(action.payload)(state);
      }
    }
    return state;
  };

  return {
    actions: duckActions as DuckActions,
    effects: duckEffects as DuckEffects,
    selectors: duckSelectors as DuckSelectors,
    reducer,
  };
};
